import { useCallback, useMemo } from 'react';

import { useToast } from '@chakra-ui/react';
import { downloadCSVWithColsAndRows } from 'Deer/intent/Reviews/hooks/useReviewsIntentElllipsisValues';
import { useLocation } from 'react-router-dom';
import { useSelectedView } from 'reducers/viewsSlice';

import { Download } from 'assets/assetsV1/iconComponents';

import { NONE_ID, fetchSurveyReportsRows } from '../EngagementSurveyReportsGrid';
import {
  useSelectEngagementSelectedDateRange,
  useSelectEngagementSelectedSplitBy,
  useSelectEngagementSelectedViewBy,
  useSelectEngagementSurveys,
} from '../slice/EngagementIntentSlice';

export const useEngagementReportIntentEllipsisValues = ({ gridRef }) => {
  const cohortDetails = useSelectedView();
  const engagementSurveys = useSelectEngagementSurveys();
  const selectedSplitBy = useSelectEngagementSelectedSplitBy();
  const selectedViewBy = useSelectEngagementSelectedViewBy();
  const { search } = useLocation();
  const toast = useToast();

  const timelineDateRange = useSelectEngagementSelectedDateRange();

  //   converting to timestamp
  const timelineRange = useMemo(
    () => ({
      startDate: timelineDateRange?.startDate?.getTime(),
      endDate: timelineDateRange?.endDate?.getTime(),
    }),
    [timelineDateRange]
  );

  const currentTableCsvExport = useCallback(() => {
    gridRef.current.api.exportDataAsCsv({
      fileName: 'engagement_survey_current_view_report.csv',
      allColumns: true,
      processCellCallback(params) {
        const { value } = params;

        return value || 'N.E.D';
      },
    });
  }, [gridRef]);

  const handleDetailedDownload = useCallback(async () => {
    if (!gridRef?.current?.api) return;

    const searchParams = new URLSearchParams(search);

    const engagementSurveyId = engagementSurveys?.find(({ uuid }) => uuid === searchParams.get('surveyId'));

    const { value: selectedSplitById, label: selectedSplitByDisplayName } = selectedSplitBy;
    const { value: selectedViewById, label: selectedViewByDisplayName } = selectedViewBy;

    const res = await fetchSurveyReportsRows({
      cohortDetails,
      engagementSurveyId,
      engagementSurveys,
      selectedViewById,
      selectedViewByDisplayName,
      selectedSplitById,
      selectedSplitByDisplayName,
      timelineRange,
      toast,
      limit: null,
    });

    const allRowsData = res?.entity?.rows ?? [];
    const columnDefs = res?.entity?.pivotFields;
    const filteredPivotFields =
      columnDefs?.filter((field: string | null | undefined) => field?.split('__')?.[1] === 'SCORE') || [];
    filteredPivotFields.unshift('QUESTION_DESCRIPTION'); // group by column

    if (selectedSplitById === NONE_ID) {
      filteredPivotFields.push('SCORE');
    }
    const formattedColumnDefs = filteredPivotFields.map((field) => ({
      colId: field,
      displayName: field,
    }));
    // Generate CSV
    downloadCSVWithColsAndRows(formattedColumnDefs, allRowsData, 'engagement_survey_all_rows_detailed_report.csv');
  }, [cohortDetails, engagementSurveys, gridRef, search, selectedSplitBy, selectedViewBy, timelineRange, toast]);

  const ellipsisOptions = useMemo(() => {
    const options = [
      {
        label: 'Download report',
        Icon: Download,
        type: 'subMenu',
        subOptions: [
          {
            label: 'Current View Download',
            onClick: () => currentTableCsvExport(),
            description: `Download of review status and metrics across employees`,
          },
          {
            label: 'Detailed Download',
            onClick: () => handleDetailedDownload(),
            description: `Download of metrics and status for all review forms across employees`,
          },
        ],
      },
    ];

    return options;
  }, [currentTableCsvExport, handleDetailedDownload]);

  return ellipsisOptions;
};
